import { Box } from '@mui/system';
import React from 'react';
import { CARD_DIMENSIONS } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/VoiceAndTone/ScatterPlot';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import colors from 'src/utils/colors';

interface BadgeProps {
  text: string;
  backgroundColor: string;
  color: string;
}

const sectionLabelSx = {
  height: '20px',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.95px',
  textTransform: 'uppercase',
  color: colors.grey
};

const sectionValueSx = {
  fontSize: '14px',
  fontWeight: 400,
  height: '20px',
  lineHeight: '20px',
  letterSpacing: '0.08px',
  textAlign: 'left'
};

interface BaseCardMetric {
  /**
   * The current value of the metric as a formatted string
   */
  value: string;
  /**
   * The percentage change in the metric as a decimal
   */
  percentChange: number;
  /**
   * The percent change as a formatted string
   */
  percentChangeFormatted: string;
}

interface ProductData {
  image: string;
  name: string;
  description: string;
}

/**
 * @deprecated
 * For backward compatibility as ContentV2 is already in use in production
 * This component will be removed in ContentV3
 */
export const Card = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        boxSizing: 'border-box',
        width: `${CARD_DIMENSIONS.width}px`,
        height: `${CARD_DIMENSIONS.height}px`,
        padding: '12px',
        borderRadius: '4px',
        border: `0.75px solid ${colors.graysSecondary}`,
        backgroundColor: '#ffffff'
      }}
    >
      <Flex flexDirection="column" gap={8}>
        {children}
      </Flex>
    </Box>
  );
};

Card.Badge = function CardBadge({ text, color, backgroundColor }: BadgeProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
        height: '24px',
        padding: '4px 8px',
        borderRadius: '4px',
        boxSizing: 'border-box',
        backgroundColor
      }}
    >
      <Text
        sx={{
          height: '18px',
          fontFamily: 'Roboto',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '18px',
          letterSpacing: '0.08px',
          color
        }}
      >
        {text}
      </Text>
    </Box>
  );
};

Card.Product = function CardProduct({ image, name, description }: ProductData) {
  return (
    <Flex alignItems="center" height={56} gap={8}>
      <img style={{ width: '40px', height: '40px' }} src={image} alt="product" />
      <Flex width={147} overflow="hidden" flexDirection="column" gap={2}>
        <Text
          sx={{
            height: '18px',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '18px',
            letterSpacing: '0.08px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {name}
        </Text>
        <Text
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '18px',
            letterSpacing: '0.08px'
          }}
        >
          {description}
        </Text>
      </Flex>
    </Flex>
  );
};

Card.MetricsSection = function CardMetricsSection({ children }: { children: React.ReactNode }) {
  return (
    <Flex height={136} gap={12}>
      {children}
    </Flex>
  );
};

Card.MetricsColumn = function CardMetricsColumn({ children }: { children: React.ReactNode }) {
  return (
    <Flex flexDirection="column" gap={8}>
      {children}
    </Flex>
  );
};

Card.Metric = function CardMetric({
  label,
  value,
  percentChange,
  percentChangeFormatted
}: { label: string } & BaseCardMetric) {
  return (
    <Flex flexDirection="column">
      <Text sx={sectionLabelSx}>{label}</Text>
      <Flex height={20} gap={8}>
        <Text sx={sectionValueSx}>{value}</Text>
        <Flex alignItems="center" gap={4}>
          <div>{getIconForMetricChange(percentChange)}</div>
          <Text sx={{ fontSize: '12px' }} variant="subtitle2" color={getColorForMetricChangeValue(percentChange)}>
            {percentChangeFormatted}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

Card.MetricInfo = function CardMetricInfo({ label, value }: { label: string; value: string }) {
  return (
    <Flex flexDirection="column">
      <Text sx={sectionLabelSx}>{label}</Text>
      <Flex height={20} gap={8}>
        <Text sx={sectionValueSx}>{value}</Text>
      </Flex>
    </Flex>
  );
};

Card.Divider = function CardDivider() {
  return <Box sx={{ width: '1px', backgroundColor: colors.opaqueGrey }} />;
};

Card.ActionButton = function ActionButton({ text, onClick }: { text: string; onClick: () => void }) {
  return (
    <Flex sx={{ height: '18px', alignItems: 'center' }}>
      <Text variant="subtitle4" link onClick={onClick}>
        <Flex alignItems="center" gap={4}>
          <span style={{ fontWeight: 500 }}>{text}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
            <g data-name="icon - right arrow link - 12px">
              <path style={{ fill: 'none' }} d="M0 0h12v12H0z" />
              <path
                data-name="↳color"
                d="m14.588 134.884-2.744-2.731a.525.525 0 0 0-.741.744l1.842 1.833h-7.42a.525.525 0 0 0 0 1.05h7.42l-1.845 1.834a.525.525 0 0 0 .741.744l2.744-2.731a.526.526 0 0 0 .003-.743z"
                transform="translate(-3.871 -129.256)"
                style={{ fill: colors.darkBlue }}
              />
            </g>
          </svg>
        </Flex>
      </Text>
    </Flex>
  );
};
